import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Bot, MessageSquare, BarChart2, Plus, 
  Search, Bell, Briefcase, LineChart, Calculator
} from 'lucide-react';

// Firebase imports
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPopup
} from 'firebase/auth';
import { auth, googleProvider } from './firebase/firebase';

import NavItem from './components/NavItem';
import UserProfileMenu from './components/UserProfileMenu';
import PortfolioView from './views/PortfolioView';
import ScreensView from './views/ScreensView';
import AuthForm from './components/AuthForm';
import ChatInterface from './components/ChatInterface';
import DCFView from './views/DCFView';

const ChatbotApp = () => {
  const [messages, setMessages] = useState([
    { role: 'bot', content: 'Hello! How can I help you today?' }
  ]);
  const [activeView, setActiveView] = useState('chat');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthProcessing, setIsAuthProcessing] = useState(false);
  
  const userMenuRef = useRef(null);

  const clearAuthForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setAuthError('');
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setIsLoggedIn(true);
        setActiveView('chat');
        clearAuthForm();
      } else {
        setUser(null);
        setIsLoggedIn(false);
        setAuthMode('signup');
      }
    });
    return () => unsubscribe();
  }, [clearAuthForm]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  const handleLogin = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      setAuthError('Please enter both email and password');
      return;
    }
    setIsAuthProcessing(true);
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message || 'Failed to log in');
    } finally {
      setIsAuthProcessing(false);
    }
  }, [email, password]);

  const handleSignup = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      setAuthError('Please enter both email and password');
      return;
    }
    if (password.length < 6) {
      setAuthError('Password should be at least 6 characters');
      return;
    }
    setIsAuthProcessing(true);
    setAuthError('');
    try {
      console.log('Attempting to create user with email:', email);
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Signup error details:', { code: error.code, message: error.message, fullError: error });
      switch (error.code) {
        case 'auth/email-already-in-use':
          setAuthError('This email is already registered. Please try logging in instead.');
          break;
        case 'auth/invalid-email':
          setAuthError('Please enter a valid email address.');
          break;
        case 'auth/operation-not-allowed':
          setAuthError('Email/password accounts are not enabled. Please contact support.');
          break;
        case 'auth/weak-password':
          setAuthError('Please choose a stronger password (at least 6 characters).');
          break;
        default:
          setAuthError(error.message || 'Failed to sign up. Please try again.');
      }
    } finally {
      setIsAuthProcessing(false);
    }
  }, [email, password]);

  const handleGoogleSignIn = useCallback(async () => {
    setIsAuthProcessing(true);
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Google sign-in error:', error);
      setAuthError(error.message || 'Failed to sign in with Google');
    } finally {
      setIsAuthProcessing(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      setUserMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  const toggleAuthMode = useCallback(() => {
    setAuthMode(prevMode => prevMode === 'login' ? 'signup' : 'login');
    setAuthError('');
  }, []);

  return (
    <div className="flex flex-col h-screen max-h-screen bg-gray-100">
      {isLoggedIn ? (
        <>
          {/* Top Navigation Bar */}
          <header className="w-full bg-white shadow z-10">
            <div className="flex justify-between items-center h-16 px-4">
              <div className="flex items-center space-x-1">
                <Bot className="text-blue-600" size={24} />
                <span className="font-bold text-xl ml-2">Fortusight.ai</span>
              </div>
              
              <div className="flex-1 max-w-lg mx-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full bg-gray-100 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button className="relative p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full">
                  <Bell size={20} />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
                <UserProfileMenu 
                  user={user}
                  userMenuOpen={userMenuOpen}
                  setUserMenuOpen={setUserMenuOpen}
                  handleLogout={handleLogout}
                  userMenuRef={userMenuRef}
                />
              </div>
            </div>
          </header>
          
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center">
                  <span className="font-semibold">Menu</span>
                </div>
              </div>
              
              <div className="p-2 flex-grow overflow-y-auto">
                <NavItem 
                  icon={<MessageSquare size={20} className="text-blue-600" />}
                  label="Start a Chat"
                  active={activeView === 'chat'}
                  onClick={() => setActiveView('chat')}
                />
                <NavItem 
                  icon={<Briefcase size={20} className="text-blue-600" />}
                  label="My Portfolio"
                  active={activeView === 'portfolio'}
                  onClick={() => setActiveView('portfolio')}
                />
                <NavItem 
                  icon={<LineChart size={20} className="text-blue-600" />}
                  label="Screens"
                  active={activeView === 'screens'}
                  onClick={() => setActiveView('screens')}
                />
                <NavItem 
                  icon={<BarChart2 size={20} className="text-blue-600" />}
                  label="Discounted Cash Flow"
                  active={activeView === 'dcf'}
                  onClick={() => setActiveView('dcf')}
                />
              </div>
              
              <div className="p-4 border-t border-gray-200">
                <button className="flex items-center justify-center w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <Plus size={18} className="mr-2" />
                  <span>New Chat</span>
                </button>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="flex-grow flex flex-col">
              <div className="bg-blue-600 p-4 text-white font-bold shadow-md">
                <div className="flex items-center">
                  {activeView === 'chat' ? (
                    <>
                      <MessageSquare className="mr-2" />
                      <span>Chat Interface</span>
                    </>
                  ) : activeView === 'portfolio' ? (
                    <>
                      <Briefcase className="mr-2" />
                      <span>Portfolio Dashboard</span>
                    </>
                  ) : activeView === 'screens' ? (
                    <>
                      <LineChart className="mr-2" />
                      <span>Stock Screens</span>
                    </>
                  ) : activeView === 'dcf' ? (
                    <>
                      <Calculator className="mr-2" />
                      <span>Discounted Cash Flow Model</span>
                    </>
                  ) : (
                    <>
                      <BarChart2 className="mr-2" />
                      <span>Modeling Tools</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex-1 min-h-0">
                {activeView === 'chat' ? (
                  <ChatInterface 
                    messages={messages} 
                    setMessages={setMessages} 
                    activeView={activeView}
                  />
                ) : activeView === 'portfolio' ? (
                  <PortfolioView />
                ) : activeView === 'screens' ? (
                  <ScreensView />
                ) : activeView === 'dcf' ? (
                  <DCFView />
                ) : (
                  <ChatInterface messages={messages} setMessages={setMessages} activeView={activeView} />
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
          <AuthForm 
            authMode={authMode}
            handleLogin={handleLogin}
            handleSignup={handleSignup}
            authError={authError}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isAuthProcessing={isAuthProcessing}
            handleGoogleSignIn={handleGoogleSignIn}
            toggleAuthMode={toggleAuthMode}
          />
        </div>
      )}
    </div>
  );
};

export default ChatbotApp;
